import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Shield } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const roles = [
  { value: "user", label: "User" },
  { value: "ticket_scanner", label: "Ticket Scanner" },
  { value: "blog_editor", label: "Blog Editor" },
  { value: "event_manager", label: "Event Manager" },
  { value: "donation_manager", label: "Donation Manager" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" }
];

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated successfully");
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const isSuperAdmin = currentUser?.role === "super_admin";

  return (
    <AdminLayout currentPage="Users">
      <div className="p-8" data-testid="admin-users">
        <div className="mb-8">
          <h1 className="text-heading-2 text-deep-blue">Users</h1>
          <p className="text-slate-600">Manage user roles and permissions</p>
        </div>

        {!isSuperAdmin && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              Only Super Admins can modify user roles.
            </p>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-20 animate-pulse"></div>
            ))}
          </div>
        ) : users.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">User</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Role</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Joined</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(user => (
                  <tr key={user.user_id} data-testid={`user-row-${user.user_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-deep-blue text-white flex items-center justify-center font-semibold">
                            {user.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-slate-800">{user.name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {isSuperAdmin && user.user_id !== currentUser?.user_id ? (
                        <Select 
                          value={user.role} 
                          onValueChange={(value) => updateRole(user.user_id, value)}
                        >
                          <SelectTrigger className="w-44" data-testid={`role-select-${user.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map(role => (
                              <SelectItem key={role.value} value={role.value}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="capitalize bg-awareness-blue/10 text-awareness-blue px-3 py-1 rounded-full text-sm font-medium">
                          {user.role?.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-slate-600">
                      {format(new Date(user.created_at), "MMM d, yyyy")}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-sm ${
                        user.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No users found</h3>
            <p className="text-slate-500">Users will appear here when they register</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
